/**
 * Unit conversions as pure functions — the logic of useUnitConversions.js with
 * no Vue in it, so the Vue composable and the React hook (Phase 1C) compute the
 * same answers from the same cached response.
 */
export type UnitConversion = { first_unit: string; second_unit: string; conversion_ratio: number }
export type ConversionsResponse =
  | { base_unit?: string | null; conversions?: unknown; [key: string]: unknown }
  | UnitConversion[]
  | null
  | undefined

function isConversion(value: unknown): value is UnitConversion {
  return !!value && typeof value === 'object' && 'first_unit' in value
}

/** The conversions array, whatever shape the response carried it in. */
export function extractConversions(data: ConversionsResponse): UnitConversion[] {
  if (!data) return []
  let conversions: unknown = []
  if (Array.isArray(data)) {
    conversions = data
  } else if (data.conversions) {
    const convs = data.conversions
    if (Array.isArray(convs)) {
      conversions = convs
    } else if (convs && typeof convs === 'object') {
      const values = Object.values(convs as Record<string, unknown>)
      if (values.some(isConversion)) {
        conversions = values
      } else {
        const arrays = values.filter(Array.isArray)
        if (arrays.length > 0) conversions = arrays[0]
      }
    }
  } else if (typeof data === 'object') {
    const arrays = Object.values(data).filter(Array.isArray)
    if (arrays.length > 0) conversions = arrays[0]
  }
  if (!Array.isArray(conversions)) {
    conversions = conversions && typeof conversions === 'object' ? Object.values(conversions as Record<string, unknown>) : []
  }
  return conversions as UnitConversion[]
}

/** The queryFn's in-place normalisation: `conversions` is an array before the response is cached. */
export function normalizeConversionsResponse<T extends ConversionsResponse>(data: T): T {
  if (data && !Array.isArray(data) && data.conversions && !Array.isArray(data.conversions)) {
    data.conversions =
      typeof data.conversions === 'object' ? Object.values(data.conversions as Record<string, unknown>) : []
  }
  return data
}

/** The API's base unit, else the most frequent `first_unit`, else null. */
export function baseUnitOf(data: ConversionsResponse, conversions: UnitConversion[]): string | null {
  const fromApi = data && !Array.isArray(data) ? data.base_unit : null
  if (fromApi) return fromApi
  const counts = new Map<string, number>()
  for (const conv of conversions) {
    if (conv?.first_unit) counts.set(conv.first_unit, (counts.get(conv.first_unit) ?? 0) + 1)
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]
  return top ? top[0] : null
}

/** The base unit first, then every unit in database order, once each. */
export function availableUnitsOf(baseUnit: string | null, conversions: UnitConversion[]): string[] {
  if (conversions.length === 0) return baseUnit ? [baseUnit] : []
  const units: string[] = []
  const seen = new Set<string>()
  const add = (unit: string | undefined | null) => {
    if (unit && !seen.has(unit)) {
      units.push(unit)
      seen.add(unit)
    }
  }
  add(baseUnit)
  for (const conv of conversions) {
    add(conv?.first_unit)
    add(conv?.second_unit)
  }
  return units
}

export type ConversionGraph = Record<string, { target: string; ratio: number }[]>

/** Adjacency list with both directions: first→second by the ratio, second→first by its inverse. */
export function buildConversionGraph(conversions: UnitConversion[]): ConversionGraph {
  const graph: ConversionGraph = {}
  for (const conv of conversions) {
    if (!conv?.first_unit || !conv.second_unit || !(conv.conversion_ratio > 0)) continue
    ;(graph[conv.first_unit] ??= []).push({ target: conv.second_unit, ratio: conv.conversion_ratio })
    ;(graph[conv.second_unit] ??= []).push({ target: conv.first_unit, ratio: 1 / conv.conversion_ratio })
  }
  return graph
}

/** Breadth-first: the multiplication factor along the first path from → to, or null. */
export function findChainedRatio(graph: ConversionGraph, from: string, to: string): number | null {
  if (!graph[from]) return null
  const queue: { unit: string; ratio: number }[] = [{ unit: from, ratio: 1 }]
  const visited = new Set([from])
  while (queue.length > 0) {
    const { unit, ratio } = queue.shift()!
    for (const edge of graph[unit] ?? []) {
      if (edge.target === to) return ratio * edge.ratio
      if (!visited.has(edge.target)) {
        visited.add(edge.target)
        queue.push({ unit: edge.target, ratio: ratio * edge.ratio })
      }
    }
  }
  return null
}

/** Direct, then reverse, then chained; 1 for the same unit; null when no path exists. */
export function conversionRatio(conversions: UnitConversion[], from: string, to: string): number | null {
  if (!from || !to || from === to) return 1
  const direct = conversions.find((c) => c.first_unit === from && c.second_unit === to)
  if (direct) return direct.conversion_ratio
  const reverse = conversions.find((c) => c.first_unit === to && c.second_unit === from)
  if (reverse) return 1 / reverse.conversion_ratio
  return findChainedRatio(buildConversionGraph(conversions), from, to)
}

/**
 * Convert a value between units, or null when there is no path.
 *
 * **null means "cannot convert"** — it used to return the value unconverted,
 * which is a lie the caller cannot detect: the Total Demand card showed a
 * base-unit number under the label of a unit it had never converted to, and two
 * node modals divided by the result and rendered `$Infinity`. Callers must
 * handle null by keeping the source unit AND saying so.
 */
export function convertSync(
  conversions: UnitConversion[],
  value: number,
  from: string,
  to: string,
): number | null {
  if (from === to) return value
  if (!value || value === 0) return 0
  const ratio = conversionRatio(conversions, from, to)
  if (ratio === null) return null
  return value * ratio
}
